import React, { useState } from 'react'
import {
  Box,
  Snackbar,
  Paper,
  Typography,
  IconButton,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  Button,
  DialogTitle,
  TablePagination,
  TextField,
} from '@mui/material'
import MuiAlert from '@mui/material/Alert'
import loadingGif from '../../bobana stok.gif'
import LoadingIndicator from 'components/shared/LoadingIndicator'

import API from '@aws-amplify/api'
import { useDispatch } from 'react-redux'
import ClearIcon from '@mui/icons-material/Clear'
import ConfirmDialog from 'components/confirmDialog'
import {
  IProduction,
  Production,
} from 'interfaces/IProduction'
import AddBatch from 'components/premix/addBatch'
import {
  STOCK_CARD_TYPE_PREMIX,
} from 'enums/stockCard'
import { formatTimestamp } from 'shared/dateFormatter'
import { Department } from 'enums/department'

interface IProps {
  department?: Department
}

export default function PremixProductionList(props: IProps) {
  const { department } = props
  const type = STOCK_CARD_TYPE_PREMIX
  const [productionData, setProductionData] = React.useState<IProduction>(
    new Production({})
  )
  const [productions, setProductions] = React.useState<IProduction[]>([])
  const [isComplete, setIsComplete] = React.useState(false)
  const [isFailureSnackbarOpen, setIsFailureSnackbarOpen] =
    React.useState(false)
  const [isSuccessSnackbarOpen, setIsSuccessSnackbarOpen] =
    React.useState(false)
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [alertMessage, setAlertMessage] = React.useState('')
  const [isCreatingPbos, setIsCreatingPbos] = React.useState(false)
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = React.useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')

  const handleSnackbarClose = (): void => {
    setIsFailureSnackbarOpen(false)
    setIsSuccessSnackbarOpen(false)
  }

  const handleDialogClose = () => {
    setIsCreatingPbos(false)
    setProductionData(new Production({}))
  }

  const getProductions = React.useCallback((
    isCompleteParam: boolean,
    pageParam: number,
    pageSizeParam: number,
    searchTermParam: string
  ) => {
    setIsLoading(true)
    API.get('products', '/production/getProductions', {
      queryStringParameters: {
        type,
        isComplete: isCompleteParam,
        page: pageParam + 1,
        pageSize: pageSizeParam,
        searchTerm: searchTermParam,
        department: department,
      },
    })
      .then((response) => {
        setProductions(response.data)
        setTotalCount(response.totalCount)
      })
      .catch((error) => {
        setIsFailureSnackbarOpen(true)
        setAlertMessage('Gagal memuat form produksi')
        console.error(error.response)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [type, department])

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
    getProductions(isComplete, newPage, rowsPerPage, searchTerm)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10)
    setRowsPerPage(newRowsPerPage)
    setPage(0)
    getProductions(isComplete, 0, newRowsPerPage, searchTerm)
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = event.target.value
    setSearchTerm(newSearchTerm)
    setPage(0)
    getProductions(isComplete, 0, rowsPerPage, newSearchTerm)
  }

  const handleDeleteProduction = () => {
    const productionIdToDelete = productionData.productionId

    setIsLoading(true)
    const apiCall = isComplete
      ? API.patch('products', `/production/cancelProduction/${productionIdToDelete}`, {})
      : API.del(
        'products',
        `/production/deleteProduction/${productionIdToDelete}`,
        {}
      )

    apiCall
      .then(() => {
        setAlertMessage(
          isComplete
            ? `Berhasil membatalkan status SELESAI untuk ${productionData.productionDisplayId}. Item dipindahkan ke tab AKTIF.`
            : `Berhasil menghapus PERMINTAAN RUAHAN ${productionData.productionDisplayId}`
        )
        setIsSuccessSnackbarOpen(true)
        getProductions(isComplete, page, rowsPerPage, searchTerm)
      })
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.message || 'Gagal menghapus form produksi'
        setAlertMessage(errorMessage)
        setIsFailureSnackbarOpen(true)
        console.error(error.response)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handleCreatePbos = (production: IProduction) => {
    setIsCreatingPbos(true)
    setProductionData(production)
  }

  const hasConfirmedStockCard =
    productionData.stockCards?.some((sc) => sc.isConfirmed) || false

  const dispatch = useDispatch()
  React.useEffect(() => {
    dispatch({
      type: 'SET_PAGE_TITLE',
      payload: `Dafar Permintaan Bikin Ruahan (PBOS)${
        department ? ` - ${department.toUpperCase()}` : ''
      }`,
    })
  }, [dispatch, department])

  React.useEffect(() => {
    setProductions([])
    setTotalCount(0)
    setPage(0)
    getProductions(isComplete, 0, rowsPerPage, searchTerm)
  }, [isComplete, getProductions, rowsPerPage, searchTerm, department])

  return (
    <Box>
      <React.Fragment>
        <Box mt={3} />
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" style={{ flexGrow: 1 }}>
            <TextField
              label="Cari PBOS / Nama Produk"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              style={{ width: 300 }}
            />
          </Box>
          <Box display="flex">
            <Button
              variant={!isComplete ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => setIsComplete(false)}
              style={{ marginRight: 8 }}
            >
              Aktif
            </Button>
            <Button
              variant={isComplete ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => setIsComplete(true)}
            >
              Riwayat
            </Button>
          </Box>
        </Box>
      </React.Fragment>
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableCell>Kode Permintaan Ruahan</TableCell>
              <TableCell>Bets Ruahan</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell>Jumlah Ruahan (kg)</TableCell>
              <TableCell>Aksi</TableCell>
            </TableHead>
            <TableBody>
              {isLoading && productions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Box py={10}>
                      <LoadingIndicator gifSrc={loadingGif} alt="loading" size={150} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                productions.map((production) => (
                  <TableRow key={production.productionId}>
                    <TableCell>
                      <Typography>{production.productionDisplayId}</Typography>
                      <Typography>
                        {formatTimestamp('id-ID', production.createdAt)}
                      </Typography>
                      {production.stockCards && production.stockCards.length > 0 && (
                        <Box mt={1}>
                          <Typography variant="caption" color="textSecondary">
                            Dokumen Terkait: {production.stockCards.map(sc => (
                              `${sc.stockCardDisplayId}${sc.isConfirmed ? ' (Sudah Konfirmasi)' : ' (Belum Konfirmasi)'}`
                            )).join(', ')}
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography>
                        {production.premixId} - {production.premixName}
                      </Typography>

                      {production.batches.map((batch) => (
                        <Box key={batch.batchNumber} mb={2}>
                          <Typography>
                            <Box fontWeight="bold" display="inline">
                              No. Bets {batch.batchNumber}
                            </Box>{' '}
                          </Typography>
                          {production.packagingId && (
                            <Typography>
                              Kode Set Kemasan: {production.packagingId}
                            </Typography>
                          )}

                          {batch.fillingAmount && (
                            <Typography>
                              Filling Amount: {batch.fillingAmount}
                              {batch.fillingUnit}
                            </Typography>
                          )}
                          {batch.packagingAmount && (
                            <Typography>
                              Jumlah Kemasan Diminta: {batch.packagingAmount}pcs
                            </Typography>
                          )}
                        </Box>
                      ))}
                      <Box>
                        {!isComplete && production.batches.length === 0 && (
                          <Button
                            aria-label="create"
                            color="primary"
                            variant="contained"
                            onClick={() => handleCreatePbos(production)}
                          >
                            Buat PBOS
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography>{production.notes}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>{Number(production.premixQuantity ?? 0).toFixed(6)}</Typography>
                    </TableCell>

                    <TableCell>
                      {isComplete ? (
                        <Button
                          variant="outlined"
                          color="secondary"
                          size="small"
                          onClick={() => {
                            setIsConfirmDeleteOpen(true)
                            setProductionData(production)
                          }}
                        >
                          Batal Selesai
                        </Button>
                      ) : (
                        <IconButton
                          aria-label="delete"
                          color="secondary"
                          onClick={() => {
                            setIsConfirmDeleteOpen(true)
                            setProductionData(production)
                          }}
                          size="large">
                          <ClearIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Paper>
      <Dialog
        fullWidth
        maxWidth="xl"
        open={isCreatingPbos}
        onClose={handleDialogClose}
      >
        <DialogTitle>
          <Typography variant="h4">Processing Bulk Order Sheet</Typography>
        </DialogTitle>
        <AddBatch
          productionData={productionData}
          handleSave={() => {
            getProductions(isComplete, page, rowsPerPage, searchTerm)
            handleDialogClose()
          }}
        />
      </Dialog>
      <Snackbar
        open={isSuccessSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="success" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={isFailureSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="error" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
      <ConfirmDialog
        title={isComplete ? 'Konfirmasi Batal Selesai' : 'Konfirmasi Hapus'}
        open={isConfirmDeleteOpen}
        setOpen={setIsConfirmDeleteOpen}
        onConfirm={() => handleDeleteProduction()}
        confirmDisabled={hasConfirmedStockCard}
      >
        <Typography variant="h6">
          {isComplete
            ? `Yakin ingin membatalkan status SELESAI untuk ${productionData.productionDisplayId}? Form akan dikembalikan ke tab AKTIF.`
            : `Yakin ingin menghapus ${productionData.productionDisplayId} dan nomor bets terkait dari system?`}
        </Typography>
        <Box mt={2}>
          <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
            Kartu Stok Terkait:
          </Typography>
          {productionData.stockCards && productionData.stockCards.length > 0 ? (
            <Box mt={1}>
              {productionData.stockCards.map((stockCard, index) => (
                <Box key={index} mb={1} pl={2}>
                  <Typography variant="body2">
                    • {stockCard.stockCardDisplayId || 'ID Pending'} - {stockCard.isConfirmed ? 'Sudah Konfirmasi' : 'Belum Konfirmasi'}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic', marginLeft: 16 }}>
              Tidak ada kartu stok terkait.
            </Typography>
          )}
        </Box>
        {hasConfirmedStockCard && (
          <Box mt={2}>
            <MuiAlert severity="warning" variant="filled">
              {isComplete
                ? 'Tidak dapat membatalkan status selesai karena terdapat kartu stok yang sudah dikonfirmasi.'
                : 'Tidak dapat menghapus permintaan produksi ini karena terdapat kartu stok yang sudah dikonfirmasi.'}
            </MuiAlert>
          </Box>
        )}
      </ConfirmDialog>
    </Box>
  );
}
